import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify: () => api.get('/auth/verify'),
  requestOTP: (data) => api.post('/auth/request-otp', data),
};

// Backups
export const backupsAPI = {
  list: (params) => api.get('/backups/list', { params }),
  stats: () => api.get('/backups/stats'),
  download: (key) => api.post('/backups/download', { key }),
  delete: (key) => api.delete('/backups/delete', { data: { key } }),
  byDate: (year, month) => api.get(`/backups/by-date/${year}${month ? `/${month}` : ''}`),
};

// Server
export const serverAPI = {
  runBackup: (type) => api.post('/server/backup/run', { type }, { timeout: 300000 }), // 5 minutos
  runSelectiveBackup: (databases, includePortainer) => api.post('/server/backup/selective', { 
    databases, 
    includePortainer 
  }, { timeout: 300000 }), // 5 minutos
  getAvailableDatabases: () => api.get('/server/databases/available'),
  cronStatus: () => api.get('/server/cron/status'),
  logs: (type, lines) => api.get(`/server/logs/${type}`, { params: { lines } }),
  dockerStatus: () => api.get('/server/docker/status'),
  dockerRestart: (serviceName) => api.post('/server/docker/restart', { serviceName }),
  info: () => api.get('/server/info'),
};

// Docker Management
export const dockerAPI = {
  // Containers
  getContainers: () => api.get('/server/docker/containers'),
  getContainer: (id) => api.get(`/server/docker/containers/${id}`),
  startContainer: (id) => api.post(`/server/docker/containers/${id}/start`),
  stopContainer: (id) => api.post(`/server/docker/containers/${id}/stop`),
  restartContainer: (id) => api.post(`/server/docker/restart`, { serviceName: id }),
  deleteContainer: (id, force = false) => api.delete(`/server/docker/containers/${id}`, { data: { force } }),
  
  // Volumes
  getVolumes: () => api.get('/server/docker/volumes'),
  deleteVolume: (name) => api.delete(`/server/docker/volumes/${name}`),
  
  // Images
  getImages: () => api.get('/server/docker/images'),
  deleteImage: (id, force = false) => api.delete(`/server/docker/images/${id}`, { data: { force } }),
  pullImage: (image) => api.post(`/server/docker/images/${encodeURIComponent(image)}/pull`),
  
  // Stacks
  getStacks: () => api.get('/server/docker/stacks'),
  redeployStack: (name) => api.post(`/server/docker/stacks/${name}/redeploy`),
  
  // Services
  getServices: (stack) => api.get('/server/docker/services', { params: { stack } }),
  getService: (id) => api.get(`/server/docker/services/${id}`),
  scaleService: (id, replicas) => api.post(`/server/docker/services/${id}/scale`, { replicas }),
  updateService: (id, forceUpdate = true) => api.post(`/server/docker/services/${id}/update`, { forceUpdate }),
  deleteService: (id) => api.delete(`/server/docker/services/${id}`),
  getServiceLogs: (id, options = {}) => api.get(`/server/docker/services/${id}/logs`, { params: { lines: options.lines || 100 } }),
  getContainerLogs: (id, options = {}) => api.get(`/server/docker/containers/${id}/logs`, { params: { lines: options.lines || 100 } }),
  
  // Traefik Services
  getTraefikServices: () => api.get('/server/docker/traefik-services'),
};

// System
export const systemAPI = {
  status: (serverId = 'local') => api.get('/system/status', { params: { serverId } }),
  processes: (sort, serverId = 'local') => api.get('/system/processes', { params: { sort, serverId } }),
  network: (serverId = 'local') => api.get('/system/network', { params: { serverId } }),
  temperature: (serverId = 'local') => api.get('/system/temperature', { params: { serverId } }),
};

// Global Config
export const configAPI = {
  getAll: () => api.get('/config'),
  get: (key) => api.get(`/config/${key}`),
  update: (key, data) => api.put(`/config/${key}`, data),
  create: (data) => api.post('/config', data),
  testAWS: () => api.post('/config/test-aws'),
};

// Backup Config
export const backupConfigAPI = {
  getHybrid: (serverId) => api.get(`/backup-config/${serverId}/hybrid`),
  getGlobalOptions: () => api.get('/backup-config/global/options'),
  get: (serverId) => api.get(`/backup-config/${serverId}`),
  create: (data) => api.post('/backup-config', data),
  update: (id, data) => api.put(`/backup-config/${id}`, data),
  delete: (id) => api.delete(`/backup-config/${id}`),
  getDatabases: (serverId) => api.get(`/backup-config/${serverId}/databases`),
  validateSchedule: (schedule) => api.post('/backup-config/validate-schedule', { schedule }),
};

export default api;